import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../shell/backofficeSidebar';
import { useAuth } from '../../../../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type OrderStats = {
  total: number;
  byStatus: Record<string, number>;
  byCountry: Record<string, number>;
  countryPerformance: Array<{
    country: string;
    totalOrders: number;
    incidentOrderCount: number;
    incidentRate: number;
    faultEmployeeRate: number;
    avgResolutionHours: number;
  }>;
  groups: { processing: number; incidents: number; conflictResolution: number; archive: number };
};

export default function OrderStatisticsPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const { authorizedFetch } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<OrderStats | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [countryFilter, setCountryFilter] = useState<string>('');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const globalAuthorizedFetch = React.useCallback(
    (input: RequestInfo | URL, init: RequestInit = {}) => {
      const headers = new Headers(init.headers || {});
      // Order statistics is intentionally global (not department-scoped).
      headers.set('x-department-slug', '');
      return authorizedFetch(input, { ...init, headers });
    },
    [authorizedFetch],
  );

  const statusEntries = useMemo(() => {
    const entries = Object.entries(stats?.byStatus || {});
    if (!statusFilter) return entries;
    return entries.filter(([status]) => status === statusFilter);
  }, [stats?.byStatus, statusFilter]);

  const loadStats = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = new URL(`${API_BASE}/sales/orders/stats`);
      if (searchQuery.trim()) url.searchParams.set('q', searchQuery.trim());
      if (countryFilter.trim()) url.searchParams.set('country', countryFilter.trim());
      if (fromDate) url.searchParams.set('from', `${fromDate}T00:00:00.000Z`);
      if (toDate) url.searchParams.set('to', `${toDate}T23:59:59.999Z`);
      const res = await globalAuthorizedFetch(url.toString());
      if (!res.ok) {
        throw new Error(t('backoffice.orders_stats_error', { defaultValue: 'Unable to load order statistics.' }));
      }
      const data = (await res.json()) as OrderStats;
      setStats(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('backoffice.orders_stats_error', { defaultValue: 'Unable to load order statistics.' }));
    } finally {
      setLoading(false);
    }
  }, [countryFilter, fromDate, globalAuthorizedFetch, searchQuery, t, toDate]);

  useEffect(() => {
    void loadStats();
  }, [loadStats]);

  return (
    <MainLayout
      withSidebar
      sidebarItems={getBackofficeSidebarItems(t)}
      activePath={location.pathname}
    >
      <main className="workspace-page">
        <section className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="workspace-page-title">
              {t('backoffice.orders_statistics_title', { defaultValue: 'Order statistics' })}
            </h1>
            <p className="workspace-page-subtitle text-slate-600">
              {t('backoffice.orders_statistics_subtitle', { defaultValue: 'Analyze order distribution, incidents, and country performance.' })}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('backoffice.orders_search_placeholder', { defaultValue: 'Search name / phone / order' })}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-900 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
            >
              <option value="">{t('common.all', { defaultValue: 'All' })}</option>
              {Object.keys(stats?.byStatus || {}).map((status) => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
            <select
              value={countryFilter}
              onChange={(e) => setCountryFilter(e.target.value)}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
            >
              <option value="">{t('backoffice.country_all', { defaultValue: 'All countries' })}</option>
              {Object.keys(stats?.byCountry || {})
                .sort((a, b) => a.localeCompare(b))
                .map((country) => (
                  <option key={country} value={country}>{country}</option>
                ))}
            </select>
            <label className="ml-2 text-xs font-semibold text-slate-600">
              {t('common.from', { defaultValue: 'From' })}
            </label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs font-semibold text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
            />
            <label className="text-xs font-semibold text-slate-600">
              {t('common.to', { defaultValue: 'To' })}
            </label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs font-semibold text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
            />
            <button
              type="button"
              onClick={() => {
                setFromDate('');
                setToDate('');
                setSearchQuery('');
                setCountryFilter('');
                setStatusFilter('');
              }}
              className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-700 hover:border-slate-400"
            >
              {t('common.clear', { defaultValue: 'Clear' })}
            </button>
          </div>
        </section>

        {error ? <p className="text-xs text-red-500">{error}</p> : null}

        <section className="space-y-3">
          {loading ? (
            <div className="rounded-xl border border-slate-200 bg-white/80 px-3 py-5 text-sm text-slate-600">
              {t('common.loading', { defaultValue: 'Loading…' })}
            </div>
          ) : stats ? (
            <>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                {[
                  { label: t('backoffice.orders_total', { defaultValue: 'Total' }), value: stats.total },
                  { label: t('backoffice.orders_view_active', { defaultValue: 'Processing' }), value: stats.groups.processing },
                  { label: t('backoffice.orders_view_incidents', { defaultValue: 'Incidents' }), value: stats.groups.incidents },
                  { label: t('backoffice.orders_view_resolution', { defaultValue: 'Conflict resolution' }), value: stats.groups.conflictResolution },
                  { label: t('backoffice.orders_view_archive', { defaultValue: 'Archive' }), value: stats.groups.archive },
                ].map((card) => (
                  <div key={card.label} className="rounded-xl border border-slate-200 bg-white/80 px-3 py-2">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{card.label}</div>
                    <div className="text-xl font-semibold text-slate-900">{card.value}</div>
                  </div>
                ))}
              </div>

              <div className="rounded-xl border border-slate-200 bg-white/80 px-3 py-2">
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  {t('backoffice.orders_by_status', { defaultValue: 'By status' })}
                </div>
                <div className="flex flex-wrap gap-2">
                  {statusEntries.map(([status, count]) => (
                    <button
                      key={status}
                      type="button"
                      onClick={() => setStatusFilter(status)}
                      className={[
                        'rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide',
                        statusFilter === status
                          ? 'border-brand-500 bg-brand-50 text-brand-800'
                          : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400',
                      ].join(' ')}
                    >
                      {status}: {count}
                    </button>
                  ))}
                  {statusFilter ? (
                    <button
                      type="button"
                      onClick={() => setStatusFilter('')}
                      className="rounded-full border border-slate-300 bg-white px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700 hover:border-slate-400"
                    >
                      {t('common.clear', { defaultValue: 'Clear' })}
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white/80 px-3 py-2">
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  {t('backoffice.orders_by_country', { defaultValue: 'By country' })}
                </div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(stats.byCountry).map(([country, count]) => (
                    <button
                      key={country}
                      type="button"
                      onClick={() => setCountryFilter(country)}
                      className={[
                        'rounded-full border px-2.5 py-1 text-[11px] font-semibold tracking-wide',
                        countryFilter === country
                          ? 'border-brand-500 bg-brand-50 text-brand-800'
                          : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400',
                      ].join(' ')}
                    >
                      {country}: {count}
                    </button>
                  ))}
                  {countryFilter ? (
                    <button
                      type="button"
                      onClick={() => setCountryFilter('')}
                      className="rounded-full border border-slate-300 bg-white px-2.5 py-1 text-[11px] font-semibold tracking-wide text-slate-700 hover:border-slate-400"
                    >
                      {t('common.clear', { defaultValue: 'Clear' })}
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white/80 px-3 py-2">
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  {t('backoffice.country_performance', { defaultValue: 'Country performance' })}
                </div>
                {stats.countryPerformance.length === 0 ? (
                  <p className="text-xs text-slate-500">{t('backoffice.orders_empty', { defaultValue: 'No orders found.' })}</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-left text-xs">
                      <thead className="border-b border-slate-200 text-[10px] uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="py-2 pr-3">{t('common.country', { defaultValue: 'Country' })}</th>
                          <th className="py-2 pr-3">{t('backoffice.orders_total', { defaultValue: 'Total' })}</th>
                          <th className="py-2 pr-3">{t('backoffice.incidents', { defaultValue: 'Incidents' })}</th>
                          <th className="py-2 pr-3">{t('backoffice.incident_rate', { defaultValue: 'Incident rate' })}</th>
                          <th className="py-2 pr-3">{t('backoffice.fault_employee_rate', { defaultValue: 'Fault employee rate' })}</th>
                          <th className="py-2 pr-3">{t('backoffice.avg_resolution_time', { defaultValue: 'Avg resolution time' })}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {stats.countryPerformance.map((row) => (
                          <tr
                            key={row.country}
                            className="cursor-pointer hover:bg-slate-50"
                            onClick={() => setCountryFilter(row.country)}
                          >
                            <td className="py-2 pr-3 font-semibold text-slate-900">{row.country}</td>
                            <td className="py-2 pr-3 text-slate-700">{row.totalOrders}</td>
                            <td className="py-2 pr-3 text-slate-700">{row.incidentOrderCount}</td>
                            <td className="py-2 pr-3 text-slate-700">{(row.incidentRate * 100).toFixed(1)}%</td>
                            <td className="py-2 pr-3 text-slate-700">{(row.faultEmployeeRate * 100).toFixed(1)}%</td>
                            <td className="py-2 pr-3 text-slate-700">{row.avgResolutionHours.toFixed(1)}h</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          ) : null}
        </section>
      </main>
    </MainLayout>
  );
}
